import { useEffect } from "react";
import { isTv } from "./nativeApp";

const TEXT_TYPES = ["text", "search", "email", "tel", "url", "number", "password"];

const isTextBox = (el: EventTarget | null): el is HTMLInputElement =>
  el instanceof HTMLInputElement && TEXT_TYPES.includes(el.type);

// On a TV, a focused text box hands the remote's arrows to the keyboard, even while it's closed,
// so focus could never leave it. Text boxes stay read-only while the remote moves past them; OK
// opens the keyboard; Done, or Back closing the keyboard, hands the arrows back.
export const useTvTextBoxes = () => {
  useEffect(() => {
    if (!isTv()) return;
    let typing: HTMLInputElement | null = null;
    let fullHeight = window.innerHeight;
    const lock = (el: HTMLInputElement) => {
      el.readOnly = true;
      typing = null;
    };

    const onFocusIn = (e: FocusEvent) => {
      if (isTextBox(e.target) && e.target !== typing) lock(e.target);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      const el = e.target;
      if (!isTextBox(el)) return;
      // An open keyboard keeps the arrows, so one reaching the page means it has closed. Fire TV's
      // keyboard covers the screen without shrinking the page, so this is the only sign there.
      if (!el.readOnly && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
        lock(el);
        return;
      }
      if (e.key !== "Enter") return;
      if (el.readOnly) {
        e.preventDefault();
        typing = el;
        el.readOnly = false;
        // Focus again inside the key press, so the keyboard opens.
        el.blur();
        el.focus();
      } else {
        // Done. After the key press, so a form still submits.
        window.setTimeout(() => lock(el));
      }
    };
    // Google TV's keyboard takes the bottom of the screen; the page back at full height means it closed.
    const onResize = () => {
      if (window.innerHeight >= fullHeight && typing) lock(typing);
      fullHeight = Math.max(fullHeight, window.innerHeight);
    };

    if (isTextBox(document.activeElement)) lock(document.activeElement);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("resize", onResize);
    };
  }, []);
};
